import React, { useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
    ReactFlow,
    Background,
    Controls,
    NodeTypes,
    useNodesState,
    useEdgesState,
    Edge as RFEdge,
    Node as RFNode,
} from '@xyflow/react';
import { MindMapNote } from "../types/mind-map-note";
import { FaFileAlt } from "react-icons/fa";
import { FaExternalLinkAlt } from "react-icons/fa";
import '@xyflow/react/dist/style.css';
import MindMapTurboNode from './ui/turbo/MindMapTurboNode';
import './ui/turbo/turbo.css';
import { getMindMapNotes } from '../api/notes';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import dagre from 'dagre';
import { useTranslation } from "react-i18next";

const nodeTypes: NodeTypes = {
    turbo: MindMapTurboNode as any,
};

const getLayoutedElements = (nodes: RFNode[], edges: RFEdge[], direction = 'TB') => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ 
        rankdir: direction,
        nodesep: 50,
        ranksep: 50,
    });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: 200, height: 50 });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            targetPosition: 'top' as any,
            sourcePosition: 'bottom' as any,
            position: {
                x: nodeWithPosition.x - 100,
                y: nodeWithPosition.y - 25,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

export const MindMapNotes: React.FC = () => {
    const [notes, setNotes] = useState<MindMapNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [nodes, setNodes, onNodesChange] = useNodesState<RFNode>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<RFEdge>([]);

    const { t } = useTranslation();

    const fetchNotes = async () => {
        try {
            setLoading(true);
            const response = await getMindMapNotes();
            setNotes(response.notes);
        } catch (err) {
            setError(t('common.errorFetchingData'));
            console.error('Error fetching mind map notes:', err);
        } finally {
            setLoading(false);
        }
    };

    const createMindMap = useCallback(() => {
        if (notes.length === 0) return;

        const initialNodes: RFNode[] = notes.map(note => ({
            id: note.id.toString(),
            type: 'turbo',
            data: {
                label: (
                    <Link to={`/notes/${note.id}`} target="_blank">
                        {note.name}
                        <FaExternalLinkAlt style={{ fontSize: '10px' }} />
                    </Link>
                ),
                icon: (<FaFileAlt />),
            },
            position: { x: 0, y: 0 },
        }));

        const initialEdges: RFEdge[] = [];
        notes.forEach(note => {
            note.related_notes.forEach(relatedId => {
                const edgeId = `${note.id}-${relatedId}`;
                const reverseEdgeId = `${relatedId}-${note.id}`;
                
                if (!initialEdges.find(e => e.id === edgeId || e.id === reverseEdgeId)) {
                    initialEdges.push({
                        id: edgeId,
                        source: note.id.toString(),
                        sourceHandle: 'bottom',
                        target: relatedId.toString(),
                        targetHandle: 'top',
                        type: 'smoothstep',
                        style: { stroke: '#1a192b', strokeWidth: 2 },
                    });
                }
            });
        });

        const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
            initialNodes,
            initialEdges
        );

        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
    }, [notes, setNodes, setEdges]);

    useEffect(() => {
        fetchNotes();
    }, []);

    useEffect(() => {
        if (notes.length > 0) {
            createMindMap();
        }
    }, [notes, createMindMap]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
                Mind Map Notes
            </Typography>
            <Box style={{ height: '600px', width: '100%' }}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={nodeTypes}
                    defaultEdgeOptions={{
                        type: 'smoothstep',
                        markerEnd: 'edge-circle',
                    }}
                    fitView
                    className="turbo-flow"
                >
                    <Background />
                    <Controls />
                    <svg>
                        <defs>
                            <linearGradient id="edge-gradient">
                                <stop offset="0%" stopColor="#ae53ba" />
                                <stop offset="100%" stopColor="#2a8af6" />
                            </linearGradient>

                            <marker
                                id="edge-circle"
                                viewBox="-5 -5 10 10"
                                refX="0"
                                refY="0"
                                markerUnits="strokeWidth"
                                markerWidth="10"
                                markerHeight="10"
                                orient="auto"
                            >
                                <circle stroke="#2a8af6" strokeOpacity="0.75" r="2" cx="0" cy="0" />
                            </marker>
                        </defs>
                    </svg>
                </ReactFlow>
            </Box>
        </Box>
    );
};
